import React, { useState } from 'react';
import PropTypes from 'prop-types';
import config from '../config';

function InteractiveQASection({ testResults, extractedText }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(false);

  const generateDynamicPlaceholder = (testResults) => {
    if (!testResults || testResults.length === 0) {
      return "e.g., What does my cholesterol level mean?";
    }
    
    const testNames = testResults.map(result => result.name.toLowerCase());
    
    if (testNames.some(name => name.includes('cholesterol'))) {
      return "e.g., What does my cholesterol level mean?";
    } else if (testNames.some(name => name.includes('glucose') || name.includes('sugar'))) {
      return "e.g., Is my blood sugar level normal?";
    } else if (testNames.some(name => name.includes('thyroid') || name.includes('tsh'))) {
      return "e.g., What do my thyroid results indicate?";
    } else if (testNames.some(name => name.includes('vitamin d'))) {
      return "e.g., Should I take vitamin D supplements?";
    } else if (testNames.some(name => name.includes('hemoglobin') || name.includes('hba1c'))) {
      return "e.g., What does my HbA1c result mean?";
    } else if (testNames.some(name => name.includes('iron') || name.includes('ferritin'))) {
      return "e.g., Do I have iron deficiency?";
    } else {
      return `e.g., What does my ${testResults[0].name} result mean?`;
    }
  };

  const createAnswerSummary = (fullAnswer) => {
    if (!fullAnswer) return '';
    
    // First, try to extract the "Quick Summary" section if it exists
    const quickSummaryMatch = fullAnswer.match(/\*\*Quick Summary[^:]*:\*\*\s*([^*]+?)(?=\n\*\*|$)/i);
    if (quickSummaryMatch) {
      return quickSummaryMatch[1].trim();
    }
    
    // If no Quick Summary section, create one from the content
    const cleanText = fullAnswer
      .replace(/\*\*/g, '') // Remove bold markdown
      .replace(/\*/g, '') // Remove italic markdown
      .replace(/###?/g, '') // Remove headers
      .replace(/- /g, '') // Remove bullet points
      .split('\n')
      .filter(line => line.trim().length > 0)
      .join(' ');
    
    // Extract the first meaningful sentence or key finding
    const sentences = cleanText.split(/[.!?]+/);
    let summary = '';
    let wordCount = 0;
    
    for (const sentence of sentences) {
      const words = sentence.trim().split(/\s+/);
      if (wordCount + words.length <= 50) {
        summary += sentence.trim() + '. ';
        wordCount += words.length;
      } else {
        // Add partial sentence if we have room
        const remainingWords = 50 - wordCount;
        if (remainingWords > 5) {
          summary += words.slice(0, remainingWords).join(' ') + '...';
        }
        break;
      }
    }
    
    return summary.trim() || cleanText.split(' ').slice(0, 50).join(' ') + '...';
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_BASE_URL}/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question,
          text: extractedText
        }),
      });

      const result = await response.json();

      if (result.success) {
        setAnswer(result.answer);
      } else {
        setError(result.error || 'Failed to get answer');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="question-section">
      <h3>💬 Ask a Question About Your Results</h3>
      <form onSubmit={handleQuestionSubmit}>
        <div className="question-input-container">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={generateDynamicPlaceholder(testResults)}
            className="question-input"
          />
          <button 
            type="submit" 
            disabled={loading || !question.trim()}
            className="question-button"
          >
            {loading ? 'Getting Answer...' : 'Ask'}
          </button>
        </div>
      </form>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {answer && (
        <div className="answer-section">
          <h4>💭 Quick Answer:</h4>
          <div className="answer-summary">
            <p className="summary-text">
              {createAnswerSummary(answer)}
            </p>
            <button 
              className="expand-button"
              onClick={() => setIsAnswerExpanded(!isAnswerExpanded)}
            >
              {isAnswerExpanded ? '▼ Show Less' : '▶ Show Full Answer'}
            </button>
          </div>
          
          {isAnswerExpanded && (
            <div className="answer-content expanded">
              <h5>📋 Detailed Answer:</h5>
              {answer.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

InteractiveQASection.propTypes = {
  testResults: PropTypes.array.isRequired,
  extractedText: PropTypes.string.isRequired,
};

export default InteractiveQASection;